import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import {
  User,
  Phone,
  Eye,
  EyeOff,
  Upload,
  FileText,
  Shield,
  UserPlus,
} from 'lucide-react';
import AuthSplitShell from '../components/AuthSplitShell';
import GoogleAuthButton from '../components/GoogleAuthButton';
import API_BASE from '../services/apiBase';

const Register = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    fullName: '',
    nameWithInitials: '',
    passportNumber: '',
    nicNumber: '',
    address: '',
    phoneNumber: '',
    hasWhatsApp: false,
    email: '',
    password: '',
    confirmPassword: '',
    agreeToTerms: false,
    idType: 'passport' // 'passport' or 'nic'
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState({
    passportPhoto: null,
    nicFront: null,
    nicBack: null
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleFileUpload = (e, fileType) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setErrors(prev => ({
          ...prev,
          [fileType]: 'Please upload an image file'
        }));
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({
          ...prev,
          [fileType]: 'File size must be less than 5MB'
        }));
        return;
      }

      setUploadedFiles(prev => ({
        ...prev,
        [fileType]: file
      }));

      // Clear error
      setErrors(prev => ({
        ...prev,
        [fileType]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields validation
    if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!formData.nameWithInitials.trim()) newErrors.nameWithInitials = 'Name with initials is required';
    if (!formData.address.trim()) newErrors.address = 'Address is required';
    if (!formData.phoneNumber.trim()) newErrors.phoneNumber = 'Phone number is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!formData.password) newErrors.password = 'Password is required';
    if (!formData.confirmPassword) newErrors.confirmPassword = 'Confirm password is required';
    if (!formData.agreeToTerms) newErrors.agreeToTerms = 'You must agree to terms and conditions';

    // ID validation
    if (formData.idType === 'passport') {
      if (!formData.passportNumber.trim()) newErrors.passportNumber = 'Passport number is required';
      if (!uploadedFiles.passportPhoto) newErrors.passportPhoto = 'Passport photo is required';
    } else {
      if (!formData.nicNumber.trim()) newErrors.nicNumber = 'NIC number is required';
      if (!uploadedFiles.nicFront) newErrors.nicFront = 'NIC front photo is required';
      if (!uploadedFiles.nicBack) newErrors.nicBack = 'NIC back photo is required';
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Phone validation
    const phoneRegex = /^[+]?[0-9\s\-()]{10,}$/;
    if (formData.phoneNumber && !phoneRegex.test(formData.phoneNumber)) {
      newErrors.phoneNumber = 'Please enter a valid phone number';
    }

    // Password validation
    if (formData.password && formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    // Confirm password validation
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const res = await fetch(`${API_BASE}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.fullName.trim(),
          email: formData.email.trim(),
          password: formData.password,
          phone: formData.phoneNumber.trim(),
          company: formData.company?.trim() || '',
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setErrors({ general: data.message || 'Registration failed. Please try again.' });
        return;
      }

      const profileData = {
        fullName: formData.fullName,
        nameWithInitials: formData.nameWithInitials,
        address: formData.address,
        phoneNumber: formData.phoneNumber,
        email: formData.email,
        hasWhatsApp: formData.hasWhatsApp,
        idType: formData.idType,
        passportNumber: formData.idType === 'passport' ? formData.passportNumber : '',
        nicNumber: formData.idType === 'nic' ? formData.nicNumber : '',
      };
      try {
        if (profileData.email) {
          localStorage.setItem(`jet_profile_${profileData.email}`, JSON.stringify(profileData));
        }
      } catch (_) {}

      navigate('/login', {
        state: {
          message: 'Registration successful! Please login with your credentials.',
        },
      });
    } catch (error) {
      console.error('Registration error:', error);
      setErrors({ general: 'Registration failed. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthSplitShell
      longForm
      title="Create your account"
      subtitle="Register for Jet Shipping Cargo customer portal"
      heroLead="Create your account and get access to fast, reliable and secure cargo solutions."
    >
      <motion.div
        className="auth-split-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45 }}
      >
        <form onSubmit={handleSubmit}>
            {/* Personal Information */}
            <div style={{ marginBottom: '2rem' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <User className="w-5 h-5 text-red-600" />
                Personal Information
              </h3>
              
              <div className="register-form-grid register-form-grid--mb">
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    className={`form-input ${errors.fullName ? 'error' : ''}`}
                    placeholder="Enter your full name"
                  />
                  {errors.fullName && <span className="error-text">{errors.fullName}</span>}
                </div>
                
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Name with Initials *
                  </label>
                  <input
                    type="text"
                    name="nameWithInitials"
                    value={formData.nameWithInitials}
                    onChange={handleInputChange}
                    className={`form-input ${errors.nameWithInitials ? 'error' : ''}`}
                    placeholder="e.g., A.B.C. Perera"
                  />
                  {errors.nameWithInitials && <span className="error-text">{errors.nameWithInitials}</span>}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Address *
                </label>
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  className={`form-textarea ${errors.address ? 'error' : ''}`}
                  placeholder="Enter your complete address"
                  rows="3"
                />
                {errors.address && <span className="error-text">{errors.address}</span>}
              </div>
            </div>

            {/* ID Information */}
            <div style={{ marginBottom: '2rem' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <FileText className="w-5 h-5 text-blue-600" />
                Identity Document
              </h3>
              
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  ID Type
                </label>
                <div className="register-id-type-row">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="idType"
                      value="passport"
                      checked={formData.idType === 'passport'}
                      onChange={handleInputChange}
                    />
                    Passport
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="idType"
                      value="nic"
                      checked={formData.idType === 'nic'}
                      onChange={handleInputChange}
                    />
                    NIC
                  </label>
                </div>
              </div>

              {formData.idType === 'passport' ? (
                <div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                      Passport Number *
                    </label>
                    <input
                      type="text"
                      name="passportNumber"
                      value={formData.passportNumber}
                      onChange={handleInputChange}
                      className={`form-input ${errors.passportNumber ? 'error' : ''}`}
                      placeholder="Enter passport number"
                    />
                    {errors.passportNumber && <span className="error-text">{errors.passportNumber}</span>}
                  </div>
                  
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                      Passport Photo *
                    </label>
                    <div style={{ border: '2px dashed #d1d5db', borderRadius: '8px', padding: '1rem', textAlign: 'center' }}>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleFileUpload(e, 'passportPhoto')}
                        style={{ display: 'none' }}
                        id="passportPhoto"
                      />
                      <label htmlFor="passportPhoto" style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
                        <Upload className="w-8 h-8 text-gray-400" />
                        <span style={{ color: '#6b7280' }}>
                          {uploadedFiles.passportPhoto ? uploadedFiles.passportPhoto.name : 'Click to upload passport photo'}
                        </span>
                      </label>
                    </div>
                    {errors.passportPhoto && <span className="error-text">{errors.passportPhoto}</span>}
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                      NIC Number *
                    </label>
                    <input
                      type="text"
                      name="nicNumber"
                      value={formData.nicNumber}
                      onChange={handleInputChange}
                      className={`form-input ${errors.nicNumber ? 'error' : ''}`}
                      placeholder="Enter NIC number"
                    />
                    {errors.nicNumber && <span className="error-text">{errors.nicNumber}</span>}
                  </div>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                        NIC Front Photo *
                      </label>
                      <div style={{ border: '2px dashed #d1d5db', borderRadius: '8px', padding: '1rem', textAlign: 'center' }}>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleFileUpload(e, 'nicFront')}
                          style={{ display: 'none' }}
                          id="nicFront"
                        />
                        <label htmlFor="nicFront" style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
                          <Upload className="w-6 h-6 text-gray-400" />
                          <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                            {uploadedFiles.nicFront ? uploadedFiles.nicFront.name : 'NIC Front'}
                          </span>
                        </label>
                      </div>
                      {errors.nicFront && <span className="error-text">{errors.nicFront}</span>}
                    </div>
                    
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                        NIC Back Photo *
                      </label>
                      <div style={{ border: '2px dashed #d1d5db', borderRadius: '8px', padding: '1rem', textAlign: 'center' }}>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleFileUpload(e, 'nicBack')}
                          style={{ display: 'none' }}
                          id="nicBack"
                        />
                        <label htmlFor="nicBack" style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
                          <Upload className="w-6 h-6 text-gray-400" />
                          <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                            {uploadedFiles.nicBack ? uploadedFiles.nicBack.name : 'NIC Back'}
                          </span>
                        </label>
                      </div>
                      {errors.nicBack && <span className="error-text">{errors.nicBack}</span>}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Contact Information */}
            <div style={{ marginBottom: '2rem' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Phone className="w-5 h-5 text-green-600" />
                Contact Information
              </h3>
              
              <div className="register-form-grid register-form-grid--mb">
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    className={`form-input ${errors.phoneNumber ? 'error' : ''}`}
                    placeholder="Enter phone number"
                  />
                  {errors.phoneNumber && <span className="error-text">{errors.phoneNumber}</span>}
                </div>
                
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`form-input ${errors.email ? 'error' : ''}`}
                    placeholder="Enter email address"
                  />
                  {errors.email && <span className="error-text">{errors.email}</span>}
                </div>
              </div>

              <div className="register-whatsapp-row">
                <input
                  type="checkbox"
                  name="hasWhatsApp"
                  checked={formData.hasWhatsApp}
                  onChange={handleInputChange}
                  id="hasWhatsApp"
                  style={{ marginTop: '0.2rem', flexShrink: 0 }}
                />
                <label htmlFor="hasWhatsApp">
                  <Phone className="w-4 h-4 text-green-600" style={{ marginTop: '0.15rem', flexShrink: 0 }} />
                  <span>This phone number has WhatsApp</span>
                </label>
              </div>
            </div>

            {/* Security */}
            <div style={{ marginBottom: '2rem' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Shield className="w-5 h-5 text-purple-600" />
                Security
              </h3>
              
              <div className="register-form-grid">
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Password *
                  </label>
                  <div className="register-password-toggle-wrap">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      className={`form-input ${errors.password ? 'error' : ''}`}
                      placeholder="Enter password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4 text-gray-400" /> : <Eye className="w-4 h-4 text-gray-400" />}
                    </button>
                  </div>
                  {errors.password && <span className="error-text">{errors.password}</span>}
                </div>
                
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Confirm Password *
                  </label>
                  <div className="register-password-toggle-wrap">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      className={`form-input ${errors.confirmPassword ? 'error' : ''}`}
                      placeholder="Confirm password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                    >
                      {showConfirmPassword ? <EyeOff className="w-4 h-4 text-gray-400" /> : <Eye className="w-4 h-4 text-gray-400" />}
                    </button>
                  </div>
                  {errors.confirmPassword && <span className="error-text">{errors.confirmPassword}</span>}
                </div>
              </div>
            </div>

            {/* Terms and Conditions */}
            <div style={{ marginBottom: '2rem' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  name="agreeToTerms"
                  checked={formData.agreeToTerms}
                  onChange={handleInputChange}
                  id="agreeToTerms"
                  style={{ marginTop: '0.25rem' }}
                />
                <label htmlFor="agreeToTerms" style={{ cursor: 'pointer', fontSize: '0.875rem', lineHeight: '1.5' }}>
                  I agree to the{' '}
                  <span className="auth-split-links" style={{ display: 'inline' }}>
                    <Link to="/terms">Terms & Conditions</Link>
                  </span>{' '}
                  and{' '}
                  <span className="auth-split-links" style={{ display: 'inline' }}>
                    <Link to="/privacy">Privacy Policy</Link>
                  </span>{' '}
                  of Jet Shipping & Logistics
                </label>
              </div>
              {errors.agreeToTerms && <span className="error-text">{errors.agreeToTerms}</span>}
            </div>

            {/* Submit Button */}
            <div style={{ textAlign: 'center' }}>
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn"
                style={{
                  opacity: isSubmitting ? 0.7 : 1,
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                }}
              >
                {isSubmitting ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <div className="auth-split-spinner" />
                    Creating account...
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <UserPlus className="w-5 h-5" />
                    Create Account
                  </div>
                )}
              </button>
            </div>
        </form>

        <div className="auth-split-divider-or" aria-hidden>
          <span>or sign up with</span>
        </div>
        <div className="auth-split-social-row auth-split-social-row--single">
          <GoogleAuthButton mode="signup" />
        </div>
      </motion.div>

      <motion.p
        className="auth-split-links"
        style={{ textAlign: 'center', marginTop: '1.5rem', marginBottom: 0, color: '#64748b' }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.15, duration: 0.4 }}
      >
        Already have an account? <Link to="/login">Login</Link>
      </motion.p>
    </AuthSplitShell>
  );
};

export default Register;
